import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { StatCard } from "../../../components/dashboard/stat-card";
import useRequest from "../../../components/hooks/use-req";
import Pagination from "../../../components/pagination";
import { formatCurrency, formatDateLong } from "../../../utils/function";
import { getPayoutAvatarUrl, PayoutRequest, PayoutRequestsResponse, PayoutStats, REJECTION_REASONS } from "./types";
import { ActionMenu, ApprovePayoutModal, PAYOUT_STATUS_OPTIONS, RejectPayoutModal, StatusBadge } from "./constant";
import { ColumnFilter, SortableHeader } from "../advertisers/constant";
import { buildPeriodRequestParams, DateRange, EMPTY_DATE_RANGE, PeriodDropdown, PeriodValue } from "../../../components/period-filter";
import { toast } from "sonner";

const RecommenderPayouts = () => {
  const authToken = localStorage.getItem("authToken");

  const [stats, setStats] = useState<PayoutStats | null>(null);
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodValue>("all_time");
  const [customRange, setCustomRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  // All sorting/filtering is server-side — see ASSUMPTION FLAGGED note on the fetch below for
  // which param names are confirmed vs best-guess.
  const [sortBy, setSortBy] = useState<"name" | "amount" | "requested_at" | "wallet_balance" | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [statusFilter, setStatusFilter] = useState<"All" | "pending" | "approved" | "rejected">("All");

  const [approveTarget, setApproveTarget] = useState<PayoutRequest | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PayoutRequest | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { makeRequest: getStats } = useRequest("/super-admin/payouts/overview", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: getPayouts } = useRequest("/super-admin/payouts", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  const { makeRequest: updatePayoutDecision } = useRequest("/super-admin/payouts", "PATCH", {
    Authorization: `Bearer ${authToken}`,
  });

  const fetchStats = useCallback(async () => {
    const [response] = await getStats();
    setStats(response || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ASSUMPTION FLAGGED: `sort_by`/`sort_order` and `status` follow the same convention already
  // used on the Advertisers/Recommenders pages — `status` is high-confidence (it's the exact
  // field name the response already returns), but `sort_by=name` for the recommender's name is
  // the same best-guess made on the Recommenders page (no single "name" field exists
  // server-side, just first/last name). All filtering/sorting is sent purely as backend params,
  // no client-side re-filtering/re-sorting fallback.
  const fetchPayouts = useCallback(
    async (pageToLoad = 1) => {
      const periodParams = buildPeriodRequestParams(period, customRange, { omitAllTime: true });
      if (!periodParams) return;

      setLoading(true);
      try {
        const params: Record<string, any> = {
          ...periodParams,
          page: pageToLoad,
          page_size: pageSize,
        };
        if (statusFilter !== "All") params.status = statusFilter;
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = sortOrder;
        }

        const [response] = await getPayouts(undefined, params);
        const data: PayoutRequestsResponse | undefined = response;
        setPayouts(data?.payouts || []);
        setPage(data?.page || pageToLoad);
        setTotalPages(data?.total_pages || 1);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period, customRange, pageSize, statusFilter, sortBy, sortOrder]
  );

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPayouts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customRange, statusFilter, sortBy, sortOrder]);

  const toggleSort = (field: "name" | "amount" | "requested_at" | "wallet_balance") => {
    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder("desc");
    } else {
      setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    }
  };

  const handlePageChange = (nextPage: number) => {
    fetchPayouts(nextPage);
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    setIsSubmitting(true);
    try {
      const [response, error] = await updatePayoutDecision(
        { decision: "approve" },
        undefined,
        String(approveTarget.id)
      );
      if (error || response?.status === false) {
        toast.error(error?.message || response?.message || "Failed to approve payout");
        return;
      }
      toast.success(response?.message || "Payout approved successfully");
      setPayouts((prev) => prev.map((p) => (p.id === approveTarget.id ? { ...p, status: "approved" } : p)));
      setApproveTarget(null);
      fetchStats();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectConfirm = async (reasonValue: string, notes: string) => {
    if (!rejectTarget) return;
    setIsSubmitting(true);
    try {
      const reasonLabel = REJECTION_REASONS.find((r) => r.value === reasonValue)?.label || reasonValue;
      const reason = notes.trim() ? `${reasonLabel}: ${notes.trim()}` : reasonLabel;

      const [response, error] = await updatePayoutDecision(
        { decision: "reject", reason },
        undefined,
        String(rejectTarget.id)
      );
      if (error || response?.status === false) {
        toast.error(error?.message || response?.message || "Failed to reject payout");
        return;
      }
      toast.success(response?.message || "Payout rejected");
      setPayouts((prev) => prev.map((p) => (p.id === rejectTarget.id ? { ...p, status: "rejected" } : p)));
      setRejectTarget(null);
      fetchStats();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div>
        <h1 className="text-xl lg:text-[28px] flex gap-2 text-black">Payout Requests</h1>
        <p className="text-[#717171] text-[14px] lg:text-[18px]">Manage payout requests submitted by recommenders.</p>
      </div>

      {/* Payout Overview */}
      <div className="bg-gradient-to-r from-[#E5F6F0] to-[#7E8B7E00] p-4 sm:p-7 mt-10 rounded-[24px]">
        <h2 className="text-[18px] text-[#003320] font-semibold mb-4">Payout Overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<ShieldCheck size={24} />}
            title="Pending Requests"
            description="Payout requests awaiting approval"
            value={stats?.pending_requests_count ?? 0}
          />
          <StatCard
            icon={<ShieldCheck size={24} />}
            title="Pending Amount"
            description="Total value of payouts currently awaiting approval."
            value={formatCurrency(stats?.pending_requests_amount || 0, "NGN", "symbol")}
          />
          <StatCard
            icon={<ShieldCheck size={24} />}
            title="Approved Today"
            description="Payout requests approved in the last 24 hours."
            value={stats?.approved_today_count ?? 0}
          />
          <StatCard
            icon={<ShieldCheck size={24} />}
            title="Rejected Today"
            description="Payout requests declined in the last 24 hours."
            value={stats?.rejected_today_count ?? 0}
          />
        </div>
      </div>

      {/* Recommender Payout Requests */}
      <div className="mt-10 bg-white rounded-[24px] p-4 sm:p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-gray-500 mb-1">
              <ShieldCheck size={18} />
            </div>
            <h2 className="text-lg font-bold text-black">Recommender Payout Requests</h2>
            <p className="text-sm text-[#65799A]">
              Manage pending withdrawal requests and process reward payouts.
            </p>
          </div>
          <PeriodDropdown value={period} onChange={setPeriod} customRange={customRange} onCustomRangeChange={setCustomRange} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00875A]"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-gray-400 text-xs uppercase tracking-wide">
                  <th className="text-left pb-3 font-medium">
                    <SortableHeader label="Recommender" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={() => toggleSort("name")} />
                  </th>
                  <th className="text-left pb-3 font-medium">
                    <SortableHeader label="Payout Requested" field="amount" sortBy={sortBy} sortOrder={sortOrder} onSort={() => toggleSort("amount")} />
                  </th>
                  <th className="text-left pb-3 font-medium">
                    <SortableHeader label="Requested On" field="requested_at" sortBy={sortBy} sortOrder={sortOrder} onSort={() => toggleSort("requested_at")} />
                  </th>
                  <th className="text-left pb-3 font-medium">
                    <SortableHeader label="Wallet Balance" field="wallet_balance" sortBy={sortBy} sortOrder={sortOrder} onSort={() => toggleSort("wallet_balance")} />
                  </th>
                  <th className="text-left pb-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      Status
                      <ColumnFilter options={PAYOUT_STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter} />
                    </span> 
                  </th>
                  <th className="text-left pb-3 font-medium">Approve/ Reject</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payouts?.map((payout) => (
                  <tr key={payout?.id}>
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={payout?.image_url || getPayoutAvatarUrl(payout)}
                          alt={`${payout.user_first_name} ${payout.user_last_name}`}
                          className="w-9 h-9 rounded-full object-cover border border-gray-100 bg-gray-50"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = getPayoutAvatarUrl(payout);
                          }}
                        />
                        <span className="font-semibold text-black whitespace-nowrap">{payout.user_first_name} {payout.user_last_name}</span>
                      </div>
                    </td>
                    <td className="py-4 pr-4 font-medium text-gray-700 whitespace-nowrap">
                      {formatCurrency(payout?.amount ?? 0, "NGN", "symbol")}
                    </td>
                    <td className="py-4 pr-4 text-gray-500 whitespace-nowrap">
                      {formatDateLong(new Date(payout?.requested_at))}
                    </td>
                    <td className="py-4 pr-4 text-gray-700 whitespace-nowrap">
                      {formatCurrency(payout?.wallet_balance, "NGN", "symbol")}
                    </td>
                    <td className="py-4 pr-4">
                      <StatusBadge status={payout.status} />
                    </td>
                    <td className="py-4 z-50">
                      <ActionMenu
                        onApprove={() => setApproveTarget(payout)}
                        onReject={() => setRejectTarget(payout)}
                      />
                    </td>
                  </tr>
                ))}
                {payouts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-gray-400">
                      No payout requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            isLoading={loading}
          />
        )}
      </div>

      {approveTarget && (
        <ApprovePayoutModal
          payout={approveTarget}
          onConfirm={handleApproveConfirm}
          onClose={() => setApproveTarget(null)}
          isSubmitting={isSubmitting}
        />
      )}

      {rejectTarget && (
        <RejectPayoutModal
          payout={rejectTarget}
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default RecommenderPayouts;
